import { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import {
  Box,
  Button,
  Icons,
  Stack,
  Tab,
  TabsNavigation,
  TabData,
  Text,
  styled,
} from '@/theme/index';
import { PartnersVisualizationProps } from '../EstimatedValue';
import { MarketValueGraph } from '../MarketValueGraph/MarketValueGraph';
import { MarketValueTable } from '../MarketValueTable/MarketValueTable';

const StyledToggleButton = styled(Button)(({ theme }) => ({
  width: '48px',
  height: '32px',

  '&.active': {
    backgroundColor: theme.palette.primary.main,
    '> svg': {
      color:
        theme.palette.customCard?.light ?? theme.palette.primary.contrastText,
    },
  },
  '&:first-of-type': {
    borderRadius: '4px 0 0 4px',
  },

  '&:nth-of-type(2)': {
    borderRadius: '0 4px 4px 0',
  },
}));

const StyledTabPanel = styled(Box)`
  flex-direction: column;
`;

const StyledTab = styled(Tab)`
  min-height: 45px;
  font-size: 0.875rem;
  font-weight: normal;
  padding: 9px 16px;

  &[data-active-tab='true'] {
    font-weight: bold;
  }
`;

const StyledText = styled(Text)(({ theme }) => ({
  color: theme.palette.customCard?.contrastText || theme.palette.grey[600],
}));

interface ValueHistoryProps {
  data: PartnersVisualizationProps[];
}

export function ValueHistory({ data }: ValueHistoryProps) {
  const { trackEvent } = useAppConfigContext();
  const [isDisplayingGraph, setIsDisplayingGraph] = useState<boolean>(true);
  const [activeTab, seActiveTab] = useState<number>(0);

  const estimatedTabs: TabData[] = data.map(
    (tabData: PartnersVisualizationProps, index: number) => ({
      label: tabData.tabName || '',
      // We are checking by the active tab index to delay echarts initialization
      // to the point where the tab is the active one, this is to avoid
      // an issue where initializaing the graph before the tab gets active
      // is rendering a graph of zero width
      content:
        isDisplayingGraph && index === activeTab ? (
          <MarketValueGraph
            data={tabData?.data}
            categories={tabData?.categories}
          />
        ) : (
          <MarketValueTable
            data={tabData?.data}
            categories={tabData?.categories?.slice().reverse()}
          />
        ),
    })
  );

  return (
    <>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', marginTop: 4.5 }}
      >
        <Stack>
          <Text variant="h3" mb={0}>
            Value History
          </Text>
          <StyledText variant="body1" fontSize="medium">
            Last 12 Months
          </StyledText>
        </Stack>
        <Stack direction="row">
          <StyledToggleButton
            variant="outlined"
            className={isDisplayingGraph ? 'active' : ''}
            onClick={() => {
              trackEvent(
                'chart view toggle',
                'engagement click',
                'estimated value section'
              );
              setIsDisplayingGraph(true);
            }}
          >
            <Icons.BarChart
              color="primary"
              sx={{ verticalAlign: 'middle', fontSize: 20 }}
            />
          </StyledToggleButton>
          <StyledToggleButton
            variant="outlined"
            className={!isDisplayingGraph ? 'active' : ''}
            onClick={() => {
              trackEvent(
                'table view toggle',
                'engagement click',
                'estimated value section'
              );
              setIsDisplayingGraph(false);
            }}
          >
            <Icons.FormatListBulleted
              color="primary"
              sx={{ verticalAlign: 'middle', fontSize: 20 }}
            />
          </StyledToggleButton>
        </Stack>
      </Stack>
      <TabsNavigation
        tabs={estimatedTabs}
        tab={StyledTab}
        onTabChanged={(activeTabIndex) => {
          let tab = '';
          activeTabIndex === 0 ? (tab = 'market') : (tab = 'rental');
          trackEvent(
            `${tab} value view toggle`,
            'engagement click',
            'estimated value section'
          );
          seActiveTab(activeTabIndex);
        }}
        tabPanel={StyledTabPanel}
        sx={{ minHeight: '45px', height: '45px', marginTop: 2.5 }}
      />
    </>
  );
}
